// MapComponent.tsx
import './PathMap.css'
import 'leaflet/dist/leaflet.css'

import React, { useEffect, useMemo, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useModal } from '../modal/Modal'
import { defaultMapCenter } from '@/env/config'
import { formatDate, getTodayRange } from '@/common/time'
import { DateRange, DateTimeRangeBody } from '@/popups/dateTimeRange'
import { Spinner } from '../spinner'
import { useAppContext } from '@/context/app'

export type PathPoint = {
	lat: number
	lng: number
	altitude: number
	angle: number
	speed: number
  time: string
}
export type Path = PathPoint[] | null

const THRESHOLD_TIME_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const THRESHOLD_DISTANCE_M = 3000; // 3 km in meters

// Haversine formula to compute distance between two points
function getDistanceMeters(a: PathPoint, b: PathPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function dividePathByTimeGap(path: Path): Path[] {
  if (!path || path.length === 0) return [];

  const subPaths: Path[] = [];
  let currentSubPath: PathPoint[] = [path[0]];

  for (let i = 1; i < path.length; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const timeDiff =
      new Date(curr.time).getTime() - new Date(prev.time).getTime();
    const distanceDiff = getDistanceMeters(prev, curr);

    if (timeDiff > THRESHOLD_TIME_MS || distanceDiff > THRESHOLD_DISTANCE_M) {
      subPaths.push(currentSubPath);
      currentSubPath = [];
    }
    currentSubPath.push(curr);
  }
  if (currentSubPath.length > 0) {
    subPaths.push(currentSubPath);
  }
  return subPaths;
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  path: Path
}

// function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
//   if (!newResponse.rows) {
//     return oldResponse
//   }
//   const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
//   const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
//   for (const newRow of newResponse.rows) {
//     imeiMap.set(newRow.imei, newRow)
//   }
//   const mergedRows = Array.from(imeiMap.values());
//   return {
//     time: newResponse.time,
//     rows: mergedRows,
//   }
// }

const getQuerySeparator = (fetchUrl: string): string => {
  return fetchUrl.includes("?") ? "&" : "?"
}

const formatQueryDate = (date?: Date): string => {
  return date ? date.toISOString() : ''
}

export const PathMapComponent = ({ services, session, fetchUrl }: {
  services: Services
  session: CurrentSession
  fetchUrl: string
}) => {
  const { translates } = useAppContext()
  const [resp, setResp] = useState<MapResponse | undefined>(undefined)
  const [range, setRange] = useState<DateRange>(getTodayRange())
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const { openModal } = useModal()

  async function refreshMap(fromDate?: Date, toDate?: Date): Promise<MapResponse> {
    var querySeparator = getQuerySeparator(fetchUrl)
    return services.session.fetchWithToken(`${fetchUrl}${querySeparator}fromDate=${formatQueryDate(fromDate)}&toDate=${formatQueryDate(toDate)}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      return newResponse
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap(range.from, range.to).then(setResp)
  }, [range])

  var paths = useMemo(() => {
    if (!resp?.path?.length) {
      return []
    }
    return dividePathByTimeGap(resp.path)
  }, [resp])

  if (resp === undefined || !translates) {
    return <Spinner />
  }

  if (paths.length === 0) {
    return <div>{translates.for('app.no_data')}</div>
  }

  function lastPoint(response: MapResponse | undefined): PathPoint | undefined {
    if (!response?.path || response.path.length === 0) {
      return undefined
    }
    return response.path[response.path.length - 1]
  }

  const firstRow = lastPoint(resp);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  return <div className='pathmap-wrapper'>
    <div className='top-icon-button' onClick={() => openModal((props) => DateTimeRangeBody({
      ...props,
      services,
      session,
      translates: session.translates,
      range,
      setter: setRange
    }))}>
      <i className='svgfont-calendar' />
      <span>
        {session.translates.for('map.from_to_datetime').replace('{{from_date}}', formatDate(range.from)).replace('{{to_date}}', formatDate(range.to))}
      </span>
    </div>
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: 'calc(100vh - 150px)', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {paths.map(path => (
        <Polyline
          positions={path?.map((row)=>([row.lat, row.lng])) || []}
          pathOptions={{ color: 'blue', weight: 4 }} // customize color/weight as you want
        />
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  </div>
}
